import { useUsersQuery } from "@/common/api/services/users";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useJwtDecode } from "@/common/hooks/useJwtDecode";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, UserPlus } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

type CreateUserFormValues = {
    email: string;
};

interface CreateUserDialogProps {
    children: React.ReactNode;
}

export const CreateUserDialog = ({ children }: CreateUserDialogProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const { createUserMutation } = useUsersQuery();
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const { getUserUuidFromToken } = useJwtDecode();

    const createUserSchema = z.object({
        email: z.string().email(t('RegisterForm.validation.invalidEmail')),
    });

    const form = useForm<CreateUserFormValues>({
        resolver: zodResolver(createUserSchema),
        defaultValues: {
            email: "",
        },
    });

    const { mutate: createUser, isPending } = useMutation({
        ...createUserMutation,
        onSuccess: (response) => {
            toast({
                title: t('users.toast.createSuccess.title'),
                description: t('users.toast.createSuccess.description'),
            });

            queryClient.invalidateQueries({ queryKey: ["users"] });
            form.reset();
            setOpen(false);

            const { accessToken } = response;
            if (accessToken) {
                const userUUID = getUserUuidFromToken(accessToken);
                if (userUUID) {
                    navigate(`/home/users/${userUUID}`);
                }
            }
        },
        onError: (error) => {
            toast({
                variant: "destructive",
                title: t('users.toast.createError.title'),
                description: t('users.toast.createError.description'),
            });
            console.error(error);
        },
    });

    const onSubmit = (data: CreateUserFormValues) => {
        const formData = {
            email: data.email,
            // При створенні користувача адміністратором пароль потрібен тільки для API
            // Користувач отримає email з інструкціями як встановити власний пароль
            // або використати соціальний вхід для доступу до системи
            password: "defaultPassword123"
        };
        createUser(formData);
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {children}
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{t('users.dialog.create.title')}</DialogTitle>
                    <DialogDescription>
                        {t('users.dialog.create.description')}
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                        <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('users.form.email')}</FormLabel>
                                    <FormControl>
                                        <Input placeholder="email@example.com" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <Button type="submit" disabled={isPending} className="w-full">
                            {isPending ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    {t('users.dialog.create.loading')}
                                </>
                            ) : (
                                <>
                                    {t('users.dialog.create.submit')}
                                    <UserPlus className="ml-2 h-4 w-4" />
                                </>
                            )}
                        </Button>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}; 